import { api } from '@/lib/api';

import type { AcademyQuestion } from '../academy/academyApi';
import type { StudyHome, StudyPracticeYearLevel } from './studyApi';

/**
 * Institution Study assignments, student side (SIR-TEA-01; study-institution-m1-design.md §7.3),
 * and the learner's own term settings (SIR-TEA-04). Only an Institution student has assignments;
 * a family learner gets an empty list.
 */

export type StudyAssignmentKind = 'tool' | 'practice_set' | 'material';
export type StudyAssignmentRecipientStatus = 'assigned' | 'started' | 'completed';

export interface StudyAssignmentResult {
  attempts: number;
  verified_correct: number;
  verified_total: number;
  tutor_turns: number;
  highest_hint_level: number;
}

export interface MyStudyAssignment {
  id: string;
  lms_enabled?: boolean;
  kind: StudyAssignmentKind;
  subject_key: string;
  year_level: number | null;
  title: string;
  instructions: string | null;
  tool_key: string | null;
  tool_title: string | null;
  due_at: string | null;
  overdue: boolean;
  assignment_status: 'published' | 'closed';
  status: StudyAssignmentRecipientStatus;
  started_at: string | null;
  completed_at: string | null;
  result: StudyAssignmentResult;
  response_text: string | null;
  requires_visibility_ack: boolean;
}

export type StudyAssignmentQuestion = AcademyQuestion & {
  attempted: boolean;
  year_level?: string | null;
};

export interface MyStudyAssignmentDetail extends MyStudyAssignment {
  practice?: { questions: StudyAssignmentQuestion[] };
  material?: {
    items: Array<{
      id: string;
      kind: 'link' | 'tool';
      title: string;
      url: string | null;
      tool_key: string | null;
      position: number;
    }>;
  };
}

export interface MyStudyAssignmentList {
  visibility_notice: { version: string; text: string };
  assignments: MyStudyAssignment[];
}

export const STUDY_ASSIGNMENTS_QUERY_KEY = ['study-assignments'] as const;
export const studyAssignmentQueryKey = (id: string) => ['study-assignments', 'detail', id] as const;

export const listMyStudyAssignments = (subjectKey?: string) =>
  api<MyStudyAssignmentList>(
    `/study/assignments${subjectKey ? `?subject_key=${encodeURIComponent(subjectKey)}` : ''}`,
  );

export const getMyStudyAssignment = (id: string) =>
  api<MyStudyAssignmentDetail>(`/study/assignments/${encodeURIComponent(id)}`);

export const startMyStudyAssignment = (id: string) =>
  api<MyStudyAssignmentDetail>(`/study/assignments/${encodeURIComponent(id)}/start`, {
    method: 'POST',
    body: { acknowledge_teacher_visibility: true },
  });

export const completeMyStudyAssignment = (id: string, responseText?: string) =>
  api<MyStudyAssignmentDetail>(`/study/assignments/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: responseText ? { response_text: responseText } : {},
  });

export const submitAssignmentPracticeAttempt = (input: {
  assignment_id: string;
  subject_key: string;
  year_level: StudyPracticeYearLevel;
  question_id: string;
  submitted: string;
}) =>
  api<{ is_correct: boolean; correct_answer: string | null }>('/study/practice/attempts', {
    method: 'POST',
    body: { ...input, attempt_kind: 'practice' },
  });

/** Records that the student opened the assigned tool (evidence row carrying the assignment id). */
export const recordAssignmentToolOpened = (input: {
  assignment_id: string;
  subject_key: string;
  tool_key: string;
}) =>
  api<{ accepted: number }>('/study/tools/events', {
    method: 'POST',
    body: {
      events: [
        {
          event_id:
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          subject_key: input.subject_key,
          tool_key: input.tool_key,
          tool_version: 'assignment-runner',
          kind: 'tool_used',
          occurred_at: new Date().toISOString(),
          assignment_id: input.assignment_id,
        },
      ],
    },
  });

/**
 * SIR-TEA-04: the learner's own Term dates. Finishing a Term uses the wrap-up flow
 * (`/study/me/rollover`, study-my-year-prd.md §5), not `POST /study/me/terms/:n/finish`.
 */
export const updateMyStudyTermDates = (
  termNumber: number,
  input: { starts_on: string; ends_on: string },
) =>
  api<StudyHome>(`/study/me/terms/${termNumber}`, {
    method: 'PATCH',
    body: { ...input, provenance: 'learner' },
  });

/** "Year 8" for the practice API; null when the question has no usable Year level. */
export function practiceYearLevel(value: string | null | undefined): StudyPracticeYearLevel | null {
  const match = /^Year (7|8|9|10|11|12)$/.exec(value ?? '');
  return match ? (`Year ${match[1]}` as StudyPracticeYearLevel) : null;
}

export function formatAssignmentDue(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}
